/**
 * Data layer for `advertiser-sub-users`. Thin typed fetch wrappers over
 * /api/v1/advertiser-sub-users. Raw `fetch()` is confined to this file per
 * the dash contract. Mirrors BE shape (see apps/api/src/routes/advertiser-sub-users.ts).
 */

export type SubUserRole = "admin" | "editor" | "viewer";
export type SubUserStatus = "invited" | "active" | "revoked";

export interface SubUserView {
	id: string;
	advertiser_id: string;
	email: string;
	name: string;
	role: SubUserRole;
	status: SubUserStatus;
	invited_at: number;
	accepted_at: number | null;
	revoked_at: number | null;
	invited_by: string;
}

export interface ListSubUsersResponse {
	advertiser_id: string;
	status: SubUserStatus | null;
	count: number;
	items: SubUserView[];
}

export interface InviteSubUserBody {
	advertiser_id: string;
	email: string;
	name?: string;
	role: SubUserRole;
}

export interface InviteSubUserResponse {
	sub_user: SubUserView;
	invite_token: string;
}

export interface RevokeSubUserResponse {
	sub_user: SubUserView;
	already_revoked?: boolean;
}

const BASE = "/api/v1/advertiser-sub-users";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	advertiserId: string,
	status?: SubUserStatus,
): Promise<ListSubUsersResponse> {
	const qs = status ? `?status=${encodeURIComponent(status)}` : "";
	const res = await fetch(
		`${BASE}/list/${encodeURIComponent(advertiserId)}${qs}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListSubUsersResponse>(res);
}

export async function invite(
	body: InviteSubUserBody,
): Promise<InviteSubUserResponse> {
	const res = await fetch(`${BASE}/invite`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<InviteSubUserResponse>(res);
}

export async function revoke(id: string): Promise<RevokeSubUserResponse> {
	const res = await fetch(`${BASE}/revoke/${encodeURIComponent(id)}`, {
		method: "POST",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<RevokeSubUserResponse>(res);
}

export async function changeRole(
	id: string,
	role: SubUserRole,
): Promise<{ sub_user: SubUserView }> {
	const res = await fetch(`${BASE}/role/${encodeURIComponent(id)}`, {
		method: "PUT",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ role }),
	});
	return parseOrThrow<{ sub_user: SubUserView }>(res);
}

export const advertiserSubUsersQueryKeys = {
	all: ["advertiser-sub-users"] as const,
	list: (advertiserId: string, status?: SubUserStatus) =>
		[
			...advertiserSubUsersQueryKeys.all,
			"list",
			advertiserId,
			status ?? "all",
		] as const,
	detail: (id: string) =>
		[...advertiserSubUsersQueryKeys.all, id] as const,
};
